import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Switch,
  Avatar,
  Divider,
  Tooltip,
  Button
} from '@mui/material';
import { Edit, ShoppingCart, DragIndicator, ChevronLeft, ChevronRight, Add } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';
import { BranchMenuCategories } from 'types/BranchMenuCategories';
import { BranchMenuProducts } from 'types/BranchMenuProducts';
import { toCurrencyFormat } from 'utils/formatter';
import { useAppDispatch } from 'redux/hook';
import { openEditProductModal, openAddProductModal } from 'redux/modalSlice';
import chaletApi from 'api/chaletApi';
import toast from 'react-hot-toast';
import { EditProductModal } from '../EditProductModal';
import { AddProductModal } from '../AddProductModal';

type GridMenuProps = {
  data: BranchMenuCategories[];
};

type FilterType = 'todos' | 'disponibles';

export const GridMenu: React.FC<GridMenuProps> = ({ data }) => {
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('todos');
  const [productId, setProductId] = useState<number | null>(null);
  const [categoryIdForNewProduct, setCategoryIdForNewProduct] = useState<number>(0);
  const [categoriesData, setCategoriesData] = useState(data);
  const [isDragging, setIsDragging] = useState(false);
  const dispatch = useAppDispatch();
  const [updateIsAvailableInBranch] = chaletApi.useUpdateIsAvailableInBranchMutation();
  const [updateBranchMenuCategory] = chaletApi.useUpdateBranchMenuCategoryMutation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  React.useEffect(() => {
    setCategoriesData(data);
  }, [data]);

  const filteredData = useMemo(() => {
    return categoriesData.map(category => ({
      ...category,
      items: category.items?.filter(product => {
        if (selectedFilter === 'disponibles') {
          return product.isAvailable;
        }
        return true; // Show all products for 'todos'
      }).sort((a, b) => {
        // Sort available products first
        if (a.isAvailable === b.isAvailable) return 0;
        return a.isAvailable ? -1 : 1;
      }) || []
    })).filter(category => category.items.length > 0);
  }, [categoriesData, selectedFilter]);

  const handleProductEdit = (id: number) => {
    dispatch(openEditProductModal());
    setProductId(id);
  };

  const handleAvailabilityToggle = async (product: BranchMenuProducts) => {
    try {
      const updatedProduct = {
        id: product.id,
        isAvailable: product.isAvailable,
      };

      await updateIsAvailableInBranch({ products: [updatedProduct] }).unwrap();
      toast.success('Disponibilidad actualizada');
    } catch (error) {
      toast.error('Error al actualizar disponibilidad');
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDragging(false);

    if (active.id !== over?.id) {
      const oldIndex = categoriesData.findIndex(item => item.id === active.id);
      const newIndex = categoriesData.findIndex(item => item.id === over?.id);

      const newCategories = arrayMove(categoriesData, oldIndex, newIndex);

      // Update correlatives based on new order
      const updatedCategories = newCategories.map((category, index) => ({
        ...category,
        correlative: index + 1
      }));

      setCategoriesData(updatedCategories);

      try {
        // Update the correlative of the moved category
        await updateBranchMenuCategory({
          id: active.id,
          body: { correlative: newIndex + 1 }
        }).unwrap();

        toast.success('Orden de categorías actualizado');
      } catch (error) {
        // Revert on error
        setCategoriesData(categoriesData);
        toast.error('Error al actualizar orden de categorías');
      }
    }
  };

  const handleDragCancel = () => {
    setIsDragging(false);
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header with title */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Categorías
        </Typography>
      </Box>

      {/* Filter Chips */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Chip
          label="Todos"
          variant={selectedFilter === 'todos' ? 'filled' : 'outlined'}
          color={selectedFilter === 'todos' ? 'primary' : 'default'}
          onClick={() => setSelectedFilter('todos')}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          label="Productos disponibles"
          variant={selectedFilter === 'disponibles' ? 'filled' : 'outlined'}
          color={selectedFilter === 'disponibles' ? 'primary' : 'default'}
          onClick={() => setSelectedFilter('disponibles')}
          sx={{ cursor: 'pointer' }}
        />
      </Stack>

      {/* Categories and Products Grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={filteredData.map(cat => cat.id)}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={3}>
            {filteredData.map((category) => (
              <SortableCategoryItem
                key={category.id}
                category={category}
                isDragging={isDragging}
                handleProductEdit={handleProductEdit}
                handleAvailabilityToggle={handleAvailabilityToggle}
                isLastCategory={category === filteredData[filteredData.length - 1]}
                onCreateProduct={(categoryId: number) => {
                  setCategoryIdForNewProduct(categoryId);
                  dispatch(openAddProductModal());
                }}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      {filteredData.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No hay productos {selectedFilter === 'disponibles' ? 'disponibles' : ''} para mostrar
          </Typography>
        </Box>
      )}

      {productId && (
        <EditProductModal
          productId={productId}
          resetProductId={() => setProductId(null)}
        />
      )}

      {categoryIdForNewProduct !== 0 && (
        <AddProductModal categoryId={categoryIdForNewProduct} />
      )}
    </Box>
  );
};

interface SortableCategoryItemProps {
  category: BranchMenuCategories;
  isDragging: boolean;
  handleProductEdit: (id: number) => void;
  handleAvailabilityToggle: (product: BranchMenuProducts) => void;
  isLastCategory: boolean;
  onCreateProduct: (categoryId: number) => void;
}

const SortableCategoryItem: React.FC<SortableCategoryItemProps> = ({
  category,
  isDragging,
  handleProductEdit,
  handleAvailabilityToggle,
  isLastCategory,
  onCreateProduct
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isCurrentlyDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isCurrentlyDragging ? transition : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: isCurrentlyDragging ? 0.9 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes}>
      <Box
        sx={{
          position: 'relative',
          pl: isDragging ? 2 : 6,  // Increased from 5 to 6 to give more space for drag icon
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          borderRadius: isDragging ? 2 : 0,
          bgcolor: isDragging ? 'action.hover' : 'transparent',
          p: isDragging ? 2 : 0,
          mx: isDragging ? 1 : 0,
        }}
      >
        <Tooltip title={isDragging ? "Suelta para reordenar" : "Arrastra para reordenar categorías"}>
          <IconButton
            {...listeners}
            sx={{
              position: 'absolute',
              left: isDragging ? -8 : 0,  // Changed from -8 to 0 for better positioning
              top: isDragging ? '50%' : 8,
              transform: isDragging ? 'translateY(-50%)' : 'none',
              zIndex: 10,  // Increased z-index to ensure it's above title
              color: isDragging ? 'primary.main' : 'text.secondary',
              bgcolor: isDragging ? 'primary.light' : 'transparent',
              '&:hover': {
                color: 'primary.main',
                bgcolor: isDragging ? 'primary.main' : 'action.hover',
                '& svg': {
                  color: isDragging ? 'white' : 'primary.main',
                },
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              size: isDragging ? 'large' : 'medium',
            }}
          >
            <DragIndicator sx={{
              fontSize: isDragging ? '1.5rem' : '1.25rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }} />
          </IconButton>
        </Tooltip>

        <CategoryHeader category={category} isDragging={isDragging} onCreateProduct={onCreateProduct} />

        {/* Collapsible Products Section */}
        <Box
          sx={{
            maxHeight: isDragging ? 0 : '400px',
            overflow: 'hidden',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isDragging ? 0 : 1,
          }}
        >
          <VirtualizedProductsList
            products={category.items}
            handleProductEdit={handleProductEdit}
            handleAvailabilityToggle={handleAvailabilityToggle}
            isDragging={isDragging}
          />
        </Box>

        {!isLastCategory && (
          <Divider
            sx={{
              mt: isDragging ? 1 : 3,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: isDragging ? 0.3 : 1,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

interface CategoryHeaderProps {
  category: BranchMenuCategories;
  isDragging: boolean;
  onCreateProduct: (categoryId: number) => void;
}

const CategoryHeader: React.FC<CategoryHeaderProps> = ({ category, isDragging, onCreateProduct }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: isDragging ? 0 : 2,
        gap: 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Typography
        variant={isDragging ? "h5" : "h6"}
        sx={{
          fontWeight: isDragging ? 700 : 600,
          flexGrow: 1,
          fontSize: isDragging ? '1.25rem' : '1.125rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          color: isDragging ? 'primary.main' : 'text.primary',
        }}
      >
        {category.name} {!isDragging && `(${category.items.length})`}
      </Typography>

      {!isDragging && (
        <Button
          startIcon={<Add />}
          variant="contained"
          size="small"
          onClick={() => onCreateProduct(category.id)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 0.75,
            background: 'linear-gradient(45deg, #ef3d32 30%, #ff6b60 90%)',
            boxShadow: '0 3px 5px 2px rgba(239, 61, 50, .3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: 'linear-gradient(45deg, #d63329 30%, #ef3d32 90%)',
              boxShadow: '0 5px 10px 2px rgba(239, 61, 50, .4)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          Crear Producto
        </Button>
      )}

      <Chip
        size={isDragging ? "medium" : "small"}
        label={`#${category.correlative}`}
        variant={isDragging ? "filled" : "outlined"}
        color={isDragging ? "primary" : "default"}
        sx={{
          fontSize: isDragging ? '0.875rem' : '0.75rem',
          height: isDragging ? 32 : 24,
          fontWeight: isDragging ? 600 : 400,
          '& .MuiChip-label': {
            px: isDragging ? 1.5 : 1,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isDragging ? 2 : 0,
        }}
      />

      <Tooltip
        title="Número que determina el orden de aparición de las categorías en el menú. Menor número = aparece primero."
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'help',
            fontSize: isDragging ? '1rem' : '0.875rem',
            width: isDragging ? 32 : 24,
            height: isDragging ? 32 : 24,
            bgcolor: isDragging ? 'info.main' : 'action.hover',
            color: isDragging ? 'white' : 'text.secondary',
            borderRadius: '50%',
            fontWeight: isDragging ? 700 : 500,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: isDragging ? 2 : 0,
            '&:hover': {
              bgcolor: isDragging ? 'info.dark' : 'info.light',
              color: isDragging ? 'white' : 'info.main',
              transform: 'scale(1.1)',
            },
          }}
        >
          ?
        </Box>
      </Tooltip>
    </Box>
  );
};

interface VirtualizedProductsListProps {
  products: BranchMenuProducts[];
  handleProductEdit: (id: number) => void;
  handleAvailabilityToggle: (product: BranchMenuProducts) => void;
  isDragging: boolean;
}

const VirtualizedProductsList: React.FC<VirtualizedProductsListProps> = ({
  products,
  handleProductEdit,
  handleAvailabilityToggle,
  isDragging,
}) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  React.useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [products]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      // Card width (280) + gap (16 from gap: 2) + padding right (16 from pr: 2) = ~312px per card
      const cardWidth = 312;
      const itemsToScroll = 3;
      const scrollAmount = cardWidth * itemsToScroll;
      
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? Math.max(0, currentScroll - scrollAmount)
        : currentScroll + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  if (products.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 200,
        color: 'text.secondary'
      }}>
        <Typography variant="body2">No hay productos en esta categoría</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: 420, pb: 2 }}>
      {/* Left Navigation Button */}
      <IconButton
        onClick={() => handleScroll('left')}
        disabled={!canScrollLeft}
        sx={{
          position: 'absolute',
          left: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          bgcolor: 'primary.main',
          color: 'white',
          boxShadow: 3,
          '&:hover': {
            bgcolor: 'primary.dark',
            transform: 'translateY(-50%) scale(1.1)',
          },
          '&.Mui-disabled': {
            opacity: 0,
            pointerEvents: 'none',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <ChevronLeft fontSize="large" />
      </IconButton>

      {/* Right Navigation Button */}
      <IconButton
        onClick={() => handleScroll('right')}
        disabled={!canScrollRight}
        sx={{
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          bgcolor: 'primary.main',
          color: 'white',
          boxShadow: 3,
          '&:hover': {
            bgcolor: 'primary.dark',
            transform: 'translateY(-50%) scale(1.1)',
          },
          '&.Mui-disabled': {
            opacity: 0,
            pointerEvents: 'none',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <ChevronRight fontSize="large" />
      </IconButton>

      {/* Scrollable Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          height: 'calc(100% - 16px)', // Subtract padding to prevent cutoff
          overflowX: 'auto',
          overflowY: 'hidden',
          px: 6, // Add padding to account for navigation buttons
          pb: 2, // Add bottom padding to prevent cutoff
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            py: 1,
            minWidth: 'max-content',
          }}
        >
          {products.map((product) => (
            <Card
              key={product.product.id}
              sx={{
                minWidth: 280,
                maxWidth: 280,
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
                transition: 'all 0.2s ease-in-out',
                opacity: product.isAvailable ? 1 : 0.6,
              }}
              onClick={() => handleProductEdit(product.product.id)}
            >
              <Box sx={{ position: 'relative' }}>
                {product.product.image ? (
                  <CardMedia
                    component="img"
                    height="140"
                    image={product.product.image}
                    alt={product.product.name}
                    sx={{ objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 140,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                    }}
                  >
                    <Avatar sx={{ width: 60, height: 60 }}>
                      <ShoppingCart />
                    </Avatar>
                  </Box>
                )}
              </Box>

              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" component="div" sx={{ mb: 1, fontSize: '1rem' }}>
                  {product.product.name}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: 40,
                  }}
                >
                  {product.product.description}
                </Typography>

                <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
                  {toCurrencyFormat(product.product.price)}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="caption"
                      color={product.isAvailable ? 'success.main' : 'error.main'}
                      sx={{ fontWeight: 500 }}
                    >
                      {product.isAvailable ? 'Disponible' : 'No disponible'}
                    </Typography>

                    <Switch
                      checked={product.isAvailable}
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAvailabilityToggle(product);
                      }}
                    />
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductEdit(product.product.id);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
        ))}
        </Box>
      </Box>
    </Box>
  );
};